import React, { createContext, useContext, ReactNode } from 'react';
import { useFavorites } from '@/hooks/useFavorites';

interface FavoriteProperty {
  id: number | string;
  code?: string;
  title: string;
  image?: string;
  price?: string;
  address?: string;
  savedAt: string;
}

interface FavoritesContextValue {
  favorites: FavoriteProperty[];
  isLoading: boolean;
  count: number;
  addFavorite: (property: Omit<FavoriteProperty, 'savedAt'>) => boolean;
  removeFavorite: (propertyId: number | string) => boolean;
  toggleFavorite: (property: Omit<FavoriteProperty, 'savedAt'>) => boolean;
  isFavorite: (propertyId: number | string) => boolean;
  clearFavorites: () => void;
  getFavorite: (propertyId: number | string) => FavoriteProperty | undefined;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

interface FavoritesProviderProps {
  children: ReactNode;
}

/**
 * Provider para gerenciar favoritos em toda a aplicação
 */
export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const favoritesData = useFavorites();

  return (
    <FavoritesContext.Provider value={favoritesData}>
      {children}
    </FavoritesContext.Provider>
  );
}

/**
 * Hook para acessar favoritos em qualquer componente
 */
export function useFavoritesContext() {
  const context = useContext(FavoritesContext);
  
  if (context === undefined) {
    throw new Error('useFavoritesContext must be used within a FavoritesProvider');
  }
  
  return context;
}

/**
 * HOC para injetar favoritos em um componente
 */
export function withFavorites<P extends object>(
  Component: React.ComponentType<P & { favorites: FavoritesContextValue }>
) {
  return function WithFavoritesComponent(props: P) {
    const favorites = useFavoritesContext();
    return <Component {...props} favorites={favorites} />;
  };
}
